import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import Header from '@components/Header/Header'
import PaymentSummaryAmount from '@components/PaymentSummaryAmount/PaymentSummaryAmount'
import PaymentSummaryDetails from '@components/PaymentSummaryDetails/PaymentSummaryDetails'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import {
  Container,
  ContentContainer,
  SummaryAmountWrapper,
  DetailsWrapper,
  DetailsTitleWrapper,
  DetailsTitle,
  ButtonWrapper,
} from './PaymentSummary.styles'
import { PpobProduct } from '@hooks/usePpobProducts'
import useBalance from '@hooks/useBalance'
import { setHeaderColor } from '@lib/telegram'
import { WHITE } from '@styles/colors'
import { toCurrencyFormat } from '@lib/utils'

type PaymentSummaryProps = {
  totalAmount?: number
  estimatedTime?: string
  phoneNumber?: string
  providerName?: string
  providerIcon?: string
  onPay?: () => void
}

const PaymentSummary = ({
  totalAmount = 65000,
  phoneNumber = '62812345678',
  providerName = 'XL Axiata',
  providerIcon,
  onPay
}: PaymentSummaryProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { data: balance, isLoading: isLoadingBalance } = useBalance()

  const state = location.state as {
    phoneNumber?: string
    providerName?: string
    providerImageUrl?: string | null
    amount?: number
    amountNet?: number
    productAmount?: string
    product?: PpobProduct
    service?: string
    accountNumber?: string
    customerName?: string
    customerDetails?: {
      name: string
      segment_power: string
      meter_no: string
      subscriber_id: string
    }
    paymentType?: 'prepaid' | 'postpaid'
    refId?: string
    customerNo?: string
    buyerSkuCode?: string
    inquiryQuote?: string
    adminFee?: number
    platformFee?: number
    fromTopUpMethod?: boolean
  } | undefined

  // Use route state if available, otherwise use props
  const actualPhoneNumber = state?.phoneNumber || phoneNumber
  const actualProviderName = state?.providerName || providerName
  const actualProviderIcon = state?.providerImageUrl !== undefined ? state?.providerImageUrl : providerIcon
  const actualAmount = state?.amount || totalAmount
  const actualAmountNet = state?.amountNet || state?.amount || totalAmount
  const product = state?.product
  const productAmount = state?.productAmount
  const currentBalance = typeof balance?.confirmed_balance === 'number' ? balance.confirmed_balance : 0
  const isInsufficientBalance = currentBalance < actualAmountNet

  // PLN specific data
  const service = state?.service
  const isPln = service === 'pln'
  const isMobileData = service === 'mobile_data'
  const isPostpaid = state?.paymentType === 'postpaid'
  const accountNumber = state?.accountNumber
  const customerDetails = state?.customerDetails
  const customerNo = isPln ? customerDetails?.subscriber_id : actualPhoneNumber
  const paymentType = state?.paymentType || 'prepaid'

  // For phone bill (postpaid), use amountNet as the billing amount since there's no productAmount
  const displayAmount = isPostpaid ? `Rp${toCurrencyFormat(actualAmount)}` : (isMobileData ? productAmount : `Rp${productAmount}`)

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  const handleTopUpPress = () => {
    // Remove fromTopUpMethod flag when navigating to top-up-method
    const { fromTopUpMethod, ...cleanState } = state || {}
    navigate('/top-up-method', {
      state: {
        returnTo: 'payment-summary',
        previousState: cleanState
      }
    })
  }

  const handleBackClick = () => {
    // If coming from TopUpMethod, go back twice to skip TopUpMethod in history
    if (state?.fromTopUpMethod) {
      navigate(-3)
    } else {
      navigate(-1)
    }
  }

  const handlePay = () => {
    if (onPay) {
      onPay()
    } else {
      // For postpaid, use buyerSkuCode and customerNo from inquiry response
      // For prepaid, use buyerSkuCode from product
      const actualBuyerSkuCode = paymentType === 'postpaid' ? state?.buyerSkuCode : product?.buyerSkuCode
      const actualCustomerNo = paymentType === 'postpaid' ? state?.customerNo : customerNo

      if (!actualBuyerSkuCode) {
        Sentry.captureMessage('Missing buyerSkuCode in payment flow', {
          level: 'error',
          tags: { feature: 'ppob', flow: 'payment-summary' },
          extra: { paymentType, product, state }
        })
        return
      }

      if (!actualCustomerNo) {
        Sentry.captureMessage('Missing customerNo in payment flow', {
          level: 'error',
          tags: { feature: 'ppob', flow: 'payment-summary' },
          extra: { paymentType, product, state }
        })
        return
      }

      // For postpaid, refId is required
      if (paymentType === 'postpaid' && !state?.refId) {
        Sentry.captureMessage('Missing refId for postpaid payment', {
          level: 'error',
          tags: { feature: 'ppob', flow: 'payment-summary' },
          extra: { paymentType, state }
        })
        return
      }

      // Navigate to authentication wrapper for PIN verification
      navigate('/authentication', {
        state: {
          operationType: paymentType === 'prepaid' ? 'prepaid-payment' : 'postpaid-payment',
          headerTitle: 'ENTER PIN',
          title: 'Enter PIN',
          description: 'Enter your PIN to confirm payment',
          successRoute: '/home',
          operationData: {
            buyerSkuCode: actualBuyerSkuCode,
            customerNo: actualCustomerNo,
            ...(paymentType === 'postpaid' && state?.inquiryQuote && { inquiryQuote: state.inquiryQuote })
          },
          useBiometrics: true
        }
      })
    }
  }

  return (
    <>
      <LoadingModal visible={isLoadingBalance} message={t('paymentSummary.loadingBalance')} />
      <Container>
        <Header
          title={t('paymentSummary.header')}
          showBackButton={true}
          onBackClick={handleBackClick}
        />
        <ContentContainer>
          <SummaryAmountWrapper>
            <PaymentSummaryAmount
              amount={actualAmountNet}
              currentBalance={currentBalance}
              onTopUpPress={handleTopUpPress}
            />
          </SummaryAmountWrapper>

          <DetailsWrapper>
            <DetailsTitleWrapper>
              <DetailsTitle>{t('paymentSummary.topUpDetails')}</DetailsTitle>
            </DetailsTitleWrapper>
            <PaymentSummaryDetails
              accountNumber={isPln ? accountNumber! : actualPhoneNumber}
              accountType={isPln ? 'account' : 'phone'}
              paymentType={isPostpaid ? 'Bill' : 'Top up'}
              idrAmount={displayAmount}
              jidrAmount={actualAmountNet}
              providerIcon={isPln ? 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/other_providers/PLN.svg' : (actualProviderIcon || undefined)}
              providerName={isPln ? 'PLN' : actualProviderName}
              customerName={isPln ? customerDetails?.name : undefined}
              segmentPower={isPln ? customerDetails?.segment_power : undefined}
              meterNo={isPln ? customerDetails?.meter_no : undefined}
              adminFee={state?.adminFee}
            // platformFee={state?.platformFee} // disabled
            />
          </DetailsWrapper>
        </ContentContainer>
        <ButtonWrapper>
          <PrimaryButton onClick={handlePay} disabled={isInsufficientBalance}>
            {t('paymentSummary.pay')}
          </PrimaryButton>
        </ButtonWrapper>
      </Container>
    </>
  )
}

export default PaymentSummary
